import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { AppStateFacade } from '@app-state';
import {
    FeatureFlag,
    INotificationSettings,
} from '@models';
import { TranslateService } from '@ngx-translate/core';

export interface INotificationPreferenceChanged {
    key: string;
    enabled: boolean;
}

type NotificationSettingsCollection = Array<keyof INotificationSettings>;

@Component({
    selector: 'ui-user-profile-notifications',
    templateUrl: './user-profile-notifications.component.html',
    styleUrls: ['./user-profile-notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiUserProfileNotificationsComponent implements OnChanges {
    @Input()
    public notificationSettings!: INotificationSettings;
    @Input()
    public controlsAccess = {} as INotificationSettings;
    @Input()
    public notificationTranslations: any;
    @Input()
    public notificationRightsWarning = `indicates you don't currently have access to receive that type of notification`;
    @Input()
    public viewOnly = false;
    @Input()
    public isLoadingMap: Record<string, boolean> = {};

    @Output()
    public notificationChanged = new EventEmitter<INotificationPreferenceChanged>();

    public keys: NotificationSettingsCollection = [];

    constructor(
        private _appState: AppStateFacade,
        private _translateService: TranslateService,
        private _liveAnnouncer: LiveAnnouncer,
    ) { }

    public get hasNotificationWarning(): boolean {
        return this.keys.reduce(
            (current: boolean, key) =>
                current || !this.controlsAccess[key],
            false,
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!!changes.notificationSettings) {
            const keys = Object.keys(this.notificationSettings) as NotificationSettingsCollection;
            const excludedKeys: NotificationSettingsCollection = [];

            const keysPerFeature = new Map<FeatureFlag, keyof INotificationSettings>([
                [FeatureFlag.Tasks, 'tasks'],
            ]);
            keysPerFeature.forEach((key, feature) => {
                if (!this._appState.settings.isFeatureEnabled(feature)) {
                    excludedKeys.push(key);
                }
            });

            this.keys = keys.filter(key => !excludedKeys.includes(key));
        }
    }

    public onChange(key: string, change: MatSlideToggleChange) {
        const isCheckedString = this._translateService.instant(`checkbox_${change.checked ? 'checked' : 'unchecked'}`);
        this._liveAnnouncer.announce(`${this.notificationTranslations[key]} ${isCheckedString}`);
        this.notificationChanged.emit({
            key,
            enabled: change.checked,
        });
    }
}
